import React, { useCallback, useEffect, useState } from 'react';
import { Table, Tag, Select, Input, Space, Typography, Button, Descriptions, Tooltip, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { documentImportAPI } from '../services/api.ts';
import { DocumentImportRow, DocumentImportDetail } from '../types.ts';

const { Text, Paragraph } = Typography;

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  analyzed: { label: 'Analizzata', color: 'blue' },
  confirmed: { label: 'Confermata', color: 'green' },
  discarded: { label: 'Scartata', color: 'default' },
  failed: { label: 'Fallita', color: 'red' },
};

/**
 * Registro delle importazioni da documento con AI (Impostazioni → API, solo admin):
 * cosa è stato caricato, con quale modello, a che costo, con quale esito.
 * Espandendo una riga si vedono i prompt inviati (senza il documento) e il JSON estratto.
 */
const DocumentImportRegistry: React.FC = () => {
  const [rows, setRows] = useState<DocumentImportRow[]>([]);
  const [total, setTotal] = useState(0);
  const [types, setTypes] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filterType, setFilterType] = useState<string | undefined>();
  const [filterStatus, setFilterStatus] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [details, setDetails] = useState<Record<number, DocumentImportDetail>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await documentImportAPI.list({
        document_type: filterType,
        status: filterStatus,
        search: search || undefined,
        skip: (page - 1) * pageSize,
        limit: pageSize,
      });
      setRows(data.items || []);
      setTotal(data.total || 0);
      setTypes(data.types || []);
    } catch (error: any) {
      message.error(error?.response?.data?.detail || 'Errore nel caricamento del registro');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus, search, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = async (id: number) => {
    if (details[id]) return;
    try {
      const data = await documentImportAPI.get(id);
      setDetails(prev => ({ ...prev, [id]: data }));
    } catch (error: any) {
      message.error(error?.response?.data?.detail || 'Errore nel caricamento del dettaglio');
    }
  };

  const columns = [
    { title: 'N.', dataIndex: 'id', width: 60 },
    {
      title: 'Data',
      dataIndex: 'created_at',
      width: 140,
      render: (v: string) => (v ? new Date(v).toLocaleString('it-IT') : '-'),
    },
    { title: 'Utente', dataIndex: 'created_by_username', width: 110, render: (v: string) => v || '-' },
    { title: 'Tipo', dataIndex: 'document_type_label', width: 170 },
    {
      title: 'File',
      dataIndex: 'file_name',
      ellipsis: true,
      render: (v: string, r: DocumentImportRow) => (
        <Tooltip title={`${r.page_count ?? '?'} pagine · ${r.file_size ? Math.round(r.file_size / 1024) : '?'} KB · ${r.source_format || ''}`}>
          {v || '-'}
        </Tooltip>
      ),
    },
    {
      title: 'Modello',
      dataIndex: 'model',
      width: 180,
      render: (v: string, r: DocumentImportRow) => v ? <Text style={{ fontSize: 12 }}>{r.provider} · {v}</Text> : '-',
    },
    {
      title: 'Token in/out',
      width: 120,
      render: (r: DocumentImportRow) => r.input_tokens != null ? `${r.input_tokens} / ${r.output_tokens ?? 0}` : '-',
    },
    {
      title: 'Costo $',
      dataIndex: 'estimated_cost_usd',
      width: 90,
      align: 'right' as const,
      render: (v: number | null) => (v != null ? v.toFixed(4) : '-'),
    },
    {
      title: 'Durata',
      dataIndex: 'duration_ms',
      width: 80,
      render: (v: number | null) => (v != null ? `${(v / 1000).toFixed(1)} s` : '-'),
    },
    {
      title: 'Esito',
      dataIndex: 'status',
      width: 110,
      render: (v: string, r: DocumentImportRow) => (
        <Tooltip title={r.error_message || undefined}>
          <Tag color={STATUS_LABELS[v]?.color || 'default'}>{STATUS_LABELS[v]?.label || v}</Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Entità',
      width: 140,
      render: (r: DocumentImportRow) => r.entity_type ? `${r.entity_type} #${r.entity_id}` : '-',
    },
  ];

  const renderExpanded = (r: DocumentImportRow) => {
    const d = details[r.id];
    if (!d) return <Text type="secondary">Caricamento…</Text>;
    return (
      <div>
        {d.error_message && (
          <Paragraph type="danger"><b>Errore:</b> {d.error_message}</Paragraph>
        )}
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="Schema">{d.schema_name} v{d.schema_version}</Descriptions.Item>
          <Descriptions.Item label="Hash file">{d.file_hash}</Descriptions.Item>
          <Descriptions.Item label="Request id">{d.request_id || '-'}</Descriptions.Item>
          <Descriptions.Item label="System prompt">
            <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: 12 }}>{d.system_prompt || '-'}</pre>
          </Descriptions.Item>
          <Descriptions.Item label="Prompt del tipo">
            <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: 12 }}>{d.user_prompt || '-'}</pre>
          </Descriptions.Item>
          <Descriptions.Item label="JSON estratto">
            <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: 12, maxHeight: 400, overflow: 'auto' }}>
              {d.extraction ? JSON.stringify(d.extraction, null, 2) : '-'}
            </pre>
          </Descriptions.Item>
          {d.matching && (
            <Descriptions.Item label="Abbinamenti">
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: 12, maxHeight: 300, overflow: 'auto' }}>
                {JSON.stringify(d.matching, null, 2)}
              </pre>
            </Descriptions.Item>
          )}
        </Descriptions>
      </div>
    );
  };

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Select
          allowClear
          placeholder="Tipo documento"
          style={{ width: 220 }}
          value={filterType}
          onChange={(v) => { setFilterType(v); setPage(1); }}
          options={types}
        />
        <Select
          allowClear
          placeholder="Esito"
          style={{ width: 150 }}
          value={filterStatus}
          onChange={(v) => { setFilterStatus(v); setPage(1); }}
          options={Object.entries(STATUS_LABELS).map(([value, s]) => ({ value, label: s.label }))}
        />
        <Input.Search
          placeholder="Nome file"
          allowClear
          style={{ width: 240 }}
          onSearch={(v) => { setSearch(v); setPage(1); }}
        />
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Aggiorna</Button>
        <Text type="secondary">{total} importazioni</Text>
      </Space>
      <Table
        size="small"
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{
          current: page, pageSize, total, showSizeChanger: true,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
        }}
        expandable={{
          expandedRowRender: renderExpanded,
          onExpand: (expanded, record) => { if (expanded) loadDetail(record.id); },
        }}
      />
    </div>
  );
};

export default DocumentImportRegistry;
